import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, IconButton, LinearProgress, Paper, Typography } from '@mui/material';

import { assertExhaustiveSwitch } from '@paragon/utilities';

import { useAppMonitor } from '../../../../monitor';
import { adoptOptimizedDesign } from '../../../../store/actions/optimizationActions';
import { getOptimizationRunForActiveComponent } from '../../../../store/selectors/optimizationProgressSelectors';
import { inViewOnlyMode } from '../../../../store/selectors/viewOnlyModeSelectors';
import { optimizationRunDismissed, OptimizationRunState } from '../../../../store/slices/optimizationProgressSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { BestTrussPreview } from './BestTrussPreview';
import { RunningCostComparison } from './RunningCostComparison';

/**
 * Reports on the optimization the panel most recently started for the active component: where the search has gotten to,
 * and the best truss it has to show for it so far -- what it looks like and what it would save. Renders nothing until a
 * run has been started.
 */
export function OptimizationProgressSection() {
  const dispatch = useAppDispatch();
  const run = useAppSelector(getOptimizationRunForActiveComponent);

  if (run == null) return null;

  const isFinished = run.status === 'completed' || run.status === 'failed';
  const searchProgress = getSearchProgress(run);

  return (
    <Paper sx={{ mt: 2, p: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
          {describeStatus(run, searchProgress)}
        </Typography>
        {isFinished && (
          <IconButton
            size="small"
            aria-label="Dismiss optimization status"
            onClick={() => dispatch(optimizationRunDismissed())}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {!isFinished && <RunProgressBar searchProgress={searchProgress} />}

      {run.status === 'failed' && (
        <Typography variant="body2" color="error.main">
          {'Error optimizing truss.'}
        </Typography>
      )}

      {run.bestTrussSoFar != null && <BestTrussPreview title={describeBestTruss(run)} truss={run.bestTrussSoFar} />}

      {run.costComparison != null && (
        <RunningCostComparison
          comparison={run.costComparison}
          // Once the optimized truss is the component's own design, the truss it replaced is no longer the current one.
          currentLabel={run.adopted ? 'Previous design' : 'Current design'}
          optimizedLabel={describeBestTruss(run)}
        />
      )}

      {run.status === 'completed' && run.bestTrussSoFar != null && <AdoptionDecision run={run} />}
    </Paper>
  );
}

/**
 * Puts the finished run's design to the user, since a completed run has changed nothing on its own: the truss becomes
 * the component's design only if they take it, and declining leaves the design they started with.
 */
function AdoptionDecision({ run }: { readonly run: OptimizationRunState }) {
  const dispatch = useAppDispatch();
  const monitor = useAppMonitor();
  const isViewOnly = useAppSelector(inViewOnlyMode);

  if (run.adopted) {
    return (
      <Typography variant="body2" color="success.main">
        Applied to {run.componentName}.
      </Typography>
    );
  }

  // Adopting rewrites the component, which a read-only viewer may not do -- what the run found is still theirs to see.
  if (isViewOnly) return null;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
      <Button
        size="small"
        onClick={() => {
          monitor.recordInteraction('Optimization panel: kept current design');
          dispatch(optimizationRunDismissed());
        }}
      >
        Keep Current
      </Button>
      <Button
        size="small"
        variant="contained"
        onClick={() => {
          monitor.recordInteraction('Optimization panel: adopted optimized design');
          void dispatch(adoptOptimizedDesign());
        }}
      >
        Adopt Optimized
      </Button>
    </Box>
  );
}

function RunProgressBar({ searchProgress }: { readonly searchProgress: SearchProgress | null }) {
  // Until the optimizer has sized its search space there is no fraction to show, only that work is happening.
  if (searchProgress == null) return <LinearProgress variant="indeterminate" />;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <LinearProgress sx={{ flex: 1 }} variant="determinate" value={searchProgress.completedFraction * 100} />
      <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {Math.round(searchProgress.completedFraction * 100)}%
      </Typography>
    </Box>
  );
}

interface SearchProgress {
  readonly currentIteration: number;
  readonly numCombinations: number;
  readonly completedFraction: number;
}

/** How far through its search the run is, or null while the optimizer has yet to say how much there is to search. */
function getSearchProgress({ progress }: OptimizationRunState): SearchProgress | null {
  const { numCombinations, currentIteration } = progress;
  if (numCombinations == null || numCombinations <= 0 || currentIteration == null) return null;

  return {
    currentIteration,
    numCombinations,
    completedFraction: currentIteration / numCombinations,
  };
}

/** Names the design the run is reporting on -- only ever the best so far until the run has finished searching. */
function describeBestTruss(run: OptimizationRunState): string {
  return run.status === 'completed' ? 'Optimized truss' : 'Best truss so far';
}

function describeStatus(run: OptimizationRunState, searchProgress: SearchProgress | null): string {
  switch (run.status) {
    case 'starting':
      return `Sending ${run.componentName} to the optimizer...`;
    case 'pending':
      return 'Queued, waiting for an optimizer to pick it up...';
    case 'inProgress':
      return searchProgress != null
        ? `Searching design ${searchProgress.currentIteration.toLocaleString()} of ${searchProgress.numCombinations.toLocaleString()}...`
        : 'Searching for a cheaper design...';
    case 'completed':
      return `Finished optimizing ${run.componentName}.`;
    case 'failed':
      return `Could not optimize ${run.componentName}.`;
    default:
      assertExhaustiveSwitch(run.status);
  }
}
